import type { ImageProps } from 'fitify-ui-onboarding/src/types/ui'

import { ALL_IMAGES, DEFAULT_IMAGE_VARIANT } from 'data/images'
import type {
  CategoryPageImages,
  CategoryPages,
  CategoryVariants,
  ImageCategories,
  ImageDefinition,
} from 'types/images'

type GetImageOptions<
  TCategory extends ImageCategories,
  TVariant extends CategoryVariants<TCategory>,
> = {
  variant?: TVariant | (string & {})
  locale?: string
}

const getImage = <
  TCategory extends ImageCategories,
  TVariant extends CategoryVariants<TCategory>,
  TPages extends CategoryPages<TCategory, TVariant>,
  TImage extends CategoryPageImages<TCategory, TVariant, TPages>,
>(
  category: TCategory,
  page: TPages,
  imageName: TImage,
  { variant, locale }: GetImageOptions<TCategory, TVariant> = {}
): ImageDefinition => {
  const resolvedVariant = (variant ?? DEFAULT_IMAGE_VARIANT) as TVariant
  const image = ALL_IMAGES[category][resolvedVariant][page][
    imageName
  ] as ImageDefinition

  if (locale) {
    return {
      ...image,
      srcSet: image.srcSet.map((src) => src.replace('{{locale}}', locale)),
    }
  }

  return image
}

export const processImageDefinition = (
  imageDefinition: ImageDefinition
): ImageProps => {
  return {
    src: imageDefinition?.srcSet[0] || '',
    srcSet:
      imageDefinition?.srcSet.map((x, i) => `${x} ${i + 1}x`).join(',') || '',
    width: imageDefinition?.width || 0,
    height: imageDefinition?.height || 0,
  }
}

export const getImageProps = <
  TCategory extends ImageCategories,
  TVariant extends CategoryVariants<TCategory>,
  TPages extends CategoryPages<TCategory, TVariant>,
  TImage extends CategoryPageImages<TCategory, TVariant, TPages>,
>(
  category: TCategory,
  page: TPages,
  imageName: TImage,
  options: GetImageOptions<TCategory, TVariant> = {}
): ImageProps => {
  const imageDefinition = getImage(category, page, imageName, options)
  return processImageDefinition(imageDefinition)
}
